import type { MaterialListSettings, MaterialMerge } from '../model/schema.js';
import type { MaterialRow } from './types.js';

type Locale = 'en' | 'de';

/** Renamed rows (U71): the own name, the generated one kept as `originalName` and in the details. */
function rename(rows: readonly MaterialRow[], settings: MaterialListSettings): MaterialRow[] {
  return rows.map((r) => {
    const name = settings.rows?.[r.baseKey]?.name?.trim();
    if (r.extraId !== undefined || name === undefined || name === '' || name === r.name) return r;
    return {
      ...r,
      name,
      originalName: r.name,
      details: [r.name, r.details].filter((d) => d !== '').join(' · '),
    };
  });
}

const FOR: Record<Locale, string> = { en: 'for', de: 'für' };

/** One merged row of `members` (all of one supplier). */
function mergedRow(
  merge: MaterialMerge,
  members: MaterialRow[],
  settings: MaterialListSettings,
  locale: Locale,
): MaterialRow {
  const first = members[0]!;
  const supplier = first.supplier;
  const rowKey = supplier.key === 'own' ? `merge:${merge.id}` : `merge:${merge.id}@${supplier.key}`;
  const count = members.reduce((n, r) => n + r.count, 0);
  const fixed = settings.rows?.[rowKey]?.spares;
  const spares = fixed ?? members.reduce((n, r) => n + r.spares, 0);
  const usedBy = members.flatMap((r) => r.usedBy);
  const details = [
    members.map((r) => `${r.count}× ${r.name}`).join(', '),
    usedBy.length > 0 ? `${FOR[locale]}: ${usedBy.join(', ')}` : '',
  ]
    .filter((d) => d !== '')
    .join(' · ');
  const row: MaterialRow = {
    rowKey,
    baseKey: `merge:${merge.id}`,
    supplier,
    category: first.category,
    name: merge.name.trim() || first.name,
    count,
    spares,
    total: count + spares,
    details,
    usedBy,
    informal: members.every((r) => r.informal),
    sourceIds: members.flatMap((r) => r.sourceIds),
    mergeId: merge.id,
    members,
  };
  if (fixed !== undefined) row.sparesFixed = true;
  return row;
}

/**
 * The user's overlay on the generated material list (U71): renamed rows and merged positions.
 * A merge takes every row whose base key it lists, one merged row per supplier (U67); a row is
 * merged at most once (the first merge wins). Order is left to the caller.
 */
export function applyMaterialOverlay(
  rows: readonly MaterialRow[],
  settings: MaterialListSettings = {},
  locale: Locale = 'en',
): MaterialRow[] {
  let rest = rename(rows, settings);
  const merged: MaterialRow[] = [];
  for (const merge of settings.merges ?? []) {
    const keys = new Set(merge.rowKeys);
    const members = rest
      .filter((r) => keys.has(r.baseKey))
      .sort((a, b) => a.name.localeCompare(b.name) || a.rowKey.localeCompare(b.rowKey));
    if (members.length === 0) continue;
    rest = rest.filter((r) => !keys.has(r.baseKey));
    const bySupplier = new Map<string, MaterialRow[]>();
    for (const m of members)
      bySupplier.set(m.supplier.key, [...(bySupplier.get(m.supplier.key) ?? []), m]);
    for (const group of bySupplier.values()) merged.push(mergedRow(merge, group, settings, locale));
  }
  return [...rest, ...merged];
}
